import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@mantine/core';
import {
  languages,
  getLanguageFromPath,
  stripLanguagePrefix,
  localizePath,
} from '@/helpers/language';

/**
 * current language by url
 * @returns {object} lng, dir, localize(path), switchPath(lng)
 */
const useLanguage = () => {
  const { pathname } = useLocation();
  const lng = getLanguageFromPath(pathname);

  return {
    lng,
    dir: languages[lng].dir,
    localize: (path) => localizePath(path, lng),
    switchPath: (target) => localizePath(stripLanguagePrefix(pathname), target),
  };
};

/**
 * sync i18n language, document direction and mantine direction with url
 * @returns {boolean} is i18n language synced with url
 */
export const useApplyLanguage = () => {
  const { lng, dir } = useLanguage();
  const { i18n, t } = useTranslation();
  const { setDirection } = useDirection();

  useLayoutEffect(() => {
    if (i18n.language !== lng) i18n.changeLanguage(lng);

    document.documentElement.lang = lng;
    document.documentElement.dir = dir;
    setDirection(dir);
  }, [lng, dir, i18n, setDirection]);

  useLayoutEffect(() => {
    document.title = t('global.siteName');
  }, [t, lng]);

  return i18n.language === lng;
};

export default useLanguage;
